'use client';
import { useMemo, useState, useEffect } from 'react';
import { routing } from '@/i18n/routing';
import { getLocalLanguage, setLocalLanguage } from '@/utils/storage';
import { useRouter, usePathname } from '@/i18n/navigation';
import { useSearchParams } from 'next/navigation';
import { useGetUserSystemSettings, useSetUserSystemSettings } from '../services/user';
import { showModalSelect } from '../components/select-modal';
import { useTranslations } from 'next-intl';
import { LANGUAGE_ICON_MAP } from '../constants';
import Image from 'next/image';
import { useAtomValue, useSetAtom } from 'jotai';
import { userSystemSettingsAtom } from '@/store/userinfo';
import { isLoginAtom } from '@/store';
import { track } from '@/utils/track';
import { LANGUAGE_TRACK_MAP, LANGUAGE_MAP as languageMap } from '@/const/language';
import { normalizeLocale } from '@/utils/locale';
import { useNormalizedLocale } from '@/hooks/use-normalized-locale';

interface UseSwitchLanguageProps {
  needSync?: boolean;
  source?: string;
}
export default function useSwitchLanguage({ needSync = true, source = 'Personal Center' }: UseSwitchLanguageProps = { needSync: true, source: 'Personal Center' }) {
  const defaultLocale = useNormalizedLocale();
  const [language, setLanguage] = useState(getLocalLanguage() || defaultLocale || '');
  const setUserSystemSettingsAtom = useSetAtom(userSystemSettingsAtom);
  const isLogin = useAtomValue(isLoginAtom);
  const router = useRouter();
  const pathName = usePathname();
  const searchParams = useSearchParams();
  const { data: userSystemSettingsData, refetch: refetchUserSystemSettings } = useGetUserSystemSettings.useQueryHook({
    params: {},
    queryConfig: {
      enabled: false,
    }
  });

  // 登录后再拉取用户系统设置
  useEffect(() => {
    if (isLogin) {
      refetchUserSystemSettings();
    }
  }, [isLogin, refetchUserSystemSettings]);


  // 同步数据到 store
  useEffect(() => {
    if (userSystemSettingsData?.userSystemSettings) {
      setUserSystemSettingsAtom(userSystemSettingsData.userSystemSettings);
    }
  }, [userSystemSettingsData, setUserSystemSettingsAtom]);

  const userSetting = useMemo(() => {
    if (userSystemSettingsData) {
      return userSystemSettingsData.userSystemSettings;
    }
    return {
      language: '',
      musicSwitch: false,
      soundEffectSwitch: false,
    };
  }, [userSystemSettingsData]);
  const { mutate: setUserSystemSettings } = useSetUserSystemSettings({
    mutationConfig: {
      onSuccess: (res) => {
        if (!res.code) {
          refetchUserSystemSettings();
        }
      },
    },
  });
  const updateSeting = (key: string, value: boolean | string) => {
    if (!isLogin) return;
    setUserSystemSettings({
      params: {
        userSystemSettings: {
          ...userSetting,
          [key]: value,
        },
      },
    });
  };
  const handleLanguageChange = (newLanguage: string) => {
    if (newLanguage === '') return;
    // const oldLanguage = getLocalLanguage();
    console.log('handleLanguageChange: ', pathName, newLanguage);
    track('profile_page_button_click', {
      current_page: source,
      button_name: LANGUAGE_TRACK_MAP[newLanguage],
    });
    setLanguage(newLanguage);
    setLocalLanguage(newLanguage);
    if (needSync && isLogin) {
      updateSeting('language', newLanguage);
    }

    if (typeof window !== 'undefined') {
      // 移除当前路径中的语言前缀（如果存在）
      let pathWithoutPrefix = pathName;

      // 获取 prefix 映射
      const prefixes = (routing.localePrefix && typeof routing.localePrefix === 'object' && 'prefixes' in routing.localePrefix)
        ? routing.localePrefix.prefixes as Record<string, string>
        : {};

      const allPrefixes = Object.values(prefixes);
      for (const prefix of allPrefixes) {
        if (pathName.startsWith(prefix)) {
          pathWithoutPrefix = pathName.slice(prefix.length) || '/';
          break;
        }
      }

      // 构建查询参数
      const queryString = searchParams.toString();
      const pathWithQuery = queryString ? `${pathWithoutPrefix}?${queryString}` : pathWithoutPrefix;

      // ✅ 使用 next-intl 的 router，传入 locale 参数进行语言切换
      // 注意：必须使用 routing.locales 中实际配置的 locale，不能使用 normalize 后的完整代码
      // 找到 routing.locales 中匹配的 locale
      const targetLocale = routing.locales.find(locale =>
        normalizeLocale(locale) === normalizeLocale(newLanguage)
      ) || routing.locales[0]; // 默认使用第一个 locale（en）

      setTimeout(() => {
        router.replace(pathWithQuery, { locale: targetLocale });
      }, 0);
    }
  };

  // const commonT = useTranslations('common');
  const t = useTranslations('personal');

  const showLanguageSelect = (back?: boolean) => {
    if (!(routing?.localePrefix as any)?.prefixes) {
      return;
    }
    const uniqueLocales = Array.from(new Set(Object.keys((routing?.localePrefix as any)?.prefixes || {}).map(normalizeLocale)));
    const options = uniqueLocales.map(item => {
      return {
        label: languageMap[item],
        // label: commonT(`language.${item}` as any),
        value: item,
        icon: <Image src={LANGUAGE_ICON_MAP[item]} className='w-[26px] h-[26px]' alt={item} width={16} height={16} />,
      };
    });
    return showModalSelect({
      title: t('settings.language.select'),
      options: options,
      value: normalizeLocale(language) || '',
      showBack: back,
      onSelect: handleLanguageChange,
    });
  };

  return { language: normalizeLocale(language), handleLanguageChange, showLanguageSelect };
}
